import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Upload, ShieldCheck, Flame, Trophy, Trash2, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL, apiGet, formatUSD, formatNum } from './ops_helpers';

const flagTone = (f) => {
  const m = {
    pf_extreme: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    dd_suspicious: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    few_trades: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    dd_high: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  };
  return m[f] || 'bg-zinc-700/40 text-zinc-400 border-zinc-700';
};

const CATS = [
  { id: 'robustos', label: 'Robustos', icon: ShieldCheck, color: 'text-emerald-400', hint: 'Equilibrio fiable: muchos trades, PF sano, DD realista.' },
  { id: 'fondeadas', label: 'Para fondeadas', icon: Trophy, color: 'text-cyan-400', hint: 'DD ≤ 5%, ≥80 trades, PF 1.3-6. Aptos para prop firms.' },
  { id: 'agresivos', label: 'Agresivos', icon: Flame, color: 'text-orange-400', hint: 'Mayor beneficio bruto (ojo: puede incluir sobreoptimizados).' },
  { id: 'descartados', label: 'Descartados', icon: Trash2, color: 'text-rose-400', hint: 'Sobreoptimizados: PF extremo, pocos trades o DD irreal.' },
];

const SetTable = ({ rows, botId, filters }) => {
  const fr = (rows || []).filter((e) => {
    const s = e.score;
    if (filters.minTrades && s.trades < filters.minTrades) return false;
    if (filters.maxDD && s.dd > filters.maxDD) return false;
    if (filters.minPF && s.pf < filters.minPF) return false;
    return true;
  });

  const exportSet = async (e) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ops/backtest/${botId}/export-set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ params: e.params }),
      });
      const d = await res.json();
      const blob = new Blob([d.set_text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${botId}_set_pass${e.pass}.set`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Set ${e.pass} exportado (.set)`);
    } catch (err) { toast.error(`Error exportando: ${err.message}`); }
  };

  if (fr.length === 0) return <p className="text-center text-zinc-500 py-6 text-sm">Sin sets que cumplan los filtros.</p>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-zinc-800">
            <TableHead className="text-zinc-500 text-xs">Pass</TableHead>
            <TableHead className="text-zinc-500 text-xs text-right">Robustez</TableHead>
            <TableHead className="text-zinc-500 text-xs text-right">Profit</TableHead>
            <TableHead className="text-zinc-500 text-xs text-right">PF</TableHead>
            <TableHead className="text-zinc-500 text-xs text-right">DD %</TableHead>
            <TableHead className="text-zinc-500 text-xs text-right">Trades</TableHead>
            <TableHead className="text-zinc-500 text-xs text-right">Recovery</TableHead>
            <TableHead className="text-zinc-500 text-xs">Flags</TableHead>
            <TableHead className="text-zinc-500 text-xs text-right">Set</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fr.map((e) => {
            const s = e.score;
            return (
              <TableRow key={e.pass} className="border-zinc-800 text-xs hover:bg-zinc-800/30" data-testid={`set-row-${e.pass}`}>
                <TableCell className="font-mono text-zinc-300">{e.pass}</TableCell>
                <TableCell className="text-right">
                  <span className={`font-bold ${s.robustness >= 80 ? 'text-emerald-400' : s.robustness >= 50 ? 'text-amber-400' : 'text-rose-400'}`}>
                    {s.robustness}
                  </span>
                </TableCell>
                <TableCell className="text-right text-emerald-400 font-semibold">{formatUSD(s.profit)}</TableCell>
                <TableCell className={`text-right ${s.pf > 8 ? 'text-rose-400' : 'text-zinc-300'}`}>{formatNum(s.pf, 2)}</TableCell>
                <TableCell className={`text-right ${s.dd > 10 ? 'text-rose-400' : 'text-zinc-300'}`}>{formatNum(s.dd, 2)}</TableCell>
                <TableCell className="text-right text-zinc-300">{s.trades}</TableCell>
                <TableCell className="text-right text-zinc-400">{formatNum(s.recovery, 1)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(s.flags || []).map((f) => (
                      <Badge key={f} variant="outline" className={`text-[9px] ${flagTone(f)}`}>{f}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" className="border-zinc-700 h-6 px-2 text-[10px]"
                    data-testid={`export-set-${e.pass}`} onClick={() => exportSet(e)}>
                    <Download className="w-3 h-3 mr-1" /> .set
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export const SniperOptimizer = ({ botId = 'sniper' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ minTrades: 0, maxDD: 0, minPF: 0 });

  const fetchLatest = useCallback(async () => {
    try {
      const d = await apiGet(`/api/ops/backtest/${botId}/latest`);
      if (d.exists) setData(d);
    } catch (e) { /* sin datos aun */ }
  }, [botId]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  const onUpload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/ops/backtest/${botId}/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || res.statusText); }
      const d = await res.json();
      setData(d);
      toast.success(`Procesados ${d.summary.total_sets} sets · ${d.summary.robust} robustos`);
    } catch (e) { toast.error(`Error al subir: ${e.message}`); }
    finally { setLoading(false); ev.target.value = ''; }
  };

  const sm = data?.summary;

  return (
    <div className="space-y-4" data-testid="sniper-optimizer">
      {/* Upload */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-zinc-200">Optimizador (CSV del Strategy Tester MT5)</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Sube el export del Optimizador. Lo clasifico en robustos / fondeadas / agresivos / descartados con score de robustez.
            </p>
          </div>
          <label className="shrink-0">
            <input type="file" accept=".csv" className="hidden" onChange={onUpload} data-testid="optimizer-upload-input" disabled={loading} />
            <span className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium cursor-pointer ${loading ? 'bg-zinc-700 text-zinc-400' : 'bg-cyan-600 hover:bg-cyan-700 text-white'}`} data-testid="optimizer-upload-btn">
              <Upload className="w-4 h-4" /> {loading ? 'Procesando…' : 'Subir CSV'}
            </span>
          </label>
        </CardContent>
      </Card>

      {!data ? (
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="p-8 text-center text-zinc-500 text-sm">
            Aún no has subido ninguna optimización. Exporta desde MT5 (Optimizador → clic derecho → Exportar a CSV) y súbela arriba.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Card className="bg-zinc-900/60 border-zinc-800"><CardContent className="p-3">
              <p className="text-[10px] uppercase text-zinc-500">Sets totales</p>
              <p className="text-xl font-bold text-zinc-100">{sm.total_sets}</p></CardContent></Card>
            <Card className="bg-zinc-900/60 border-emerald-900/40"><CardContent className="p-3">
              <p className="text-[10px] uppercase text-zinc-500">Robustos</p>
              <p className="text-xl font-bold text-emerald-400">{sm.robust}</p></CardContent></Card>
            <Card className="bg-zinc-900/60 border-rose-900/40"><CardContent className="p-3">
              <p className="text-[10px] uppercase text-zinc-500">Sobreoptimizados</p>
              <p className="text-xl font-bold text-rose-400">{sm.overfit}</p></CardContent></Card>
            <Card className="bg-zinc-900/60 border-zinc-800"><CardContent className="p-3">
              <p className="text-[10px] uppercase text-zinc-500">% Robustos</p>
              <p className="text-xl font-bold text-cyan-400">{formatNum(sm.robust / sm.total_sets * 100, 1)}%</p></CardContent></Card>
          </div>

          <Card className="bg-amber-950/20 border-amber-900/40"><CardContent className="p-3 flex items-center gap-2 text-xs text-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
            <span>Un PF muy alto (&gt;8) con DD casi 0 suele ser <strong>sobreoptimización</strong>: brilla en el pasado y falla en real. Prioriza los <strong>Robustos</strong>/<strong>Fondeadas</strong>.</span>
          </CardContent></Card>

          {/* Filtros */}
          <Card className="bg-zinc-900/60 border-zinc-800"><CardContent className="p-3 flex flex-wrap items-center gap-4 text-xs">
            <span className="text-zinc-400">Filtros:</span>
            <label className="flex items-center gap-1 text-zinc-400">Min trades
              <input type="number" className="w-20 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-zinc-200" data-testid="filter-min-trades"
                value={filters.minTrades} onChange={(e) => setFilters({ ...filters, minTrades: Number(e.target.value) })} /></label>
            <label className="flex items-center gap-1 text-zinc-400">Max DD %
              <input type="number" step="0.1" className="w-20 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-zinc-200" data-testid="filter-max-dd"
                value={filters.maxDD} onChange={(e) => setFilters({ ...filters, maxDD: Number(e.target.value) })} /></label>
            <label className="flex items-center gap-1 text-zinc-400">Min PF
              <input type="number" step="0.1" className="w-20 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-zinc-200" data-testid="filter-min-pf"
                value={filters.minPF} onChange={(e) => setFilters({ ...filters, minPF: Number(e.target.value) })} /></label>
          </CardContent></Card>

          {/* Tabs por categoría */}
          <Tabs defaultValue="robustos" className="space-y-3">
            <TabsList className="bg-zinc-900/60 border border-zinc-800 flex-wrap h-auto">
              {CATS.map((c) => (
                <TabsTrigger key={c.id} value={c.id} data-testid={`cat-tab-${c.id}`}>
                  <c.icon className={`w-4 h-4 mr-1 ${c.color}`} /> {c.label} ({(data[c.id] || []).length})
                </TabsTrigger>
              ))}
            </TabsList>
            {CATS.map((c) => (
              <TabsContent key={c.id} value={c.id}>
                <Card className="bg-zinc-900/60 border-zinc-800">
                  <CardHeader className="pb-2"><CardTitle className="text-sm text-zinc-200 flex items-center gap-2">
                    <c.icon className={`w-4 h-4 ${c.color}`} /> {c.label}
                    <span className="text-xs font-normal text-zinc-500">— {c.hint}</span>
                  </CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <SetTable rows={data[c.id]} botId={botId} filters={filters} />
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}
    </div>
  );
};

export default SniperOptimizer;
